import { HStack, Skeleton } from '@chakra-ui/react';
import { Button, Tag } from 'Atoms';
import { LinkAnchor } from 'Molecules/LinkAnchor';
import { DownloadIcon, UploadIcon } from 'Tokens/Icons/Function';
import { getMetricRefNumber } from '../../Metrics.hooks';
import { MetricsTableData } from '../..';
import { useToast } from 'utils/hooks';
import { useExportEsrsMetricTable } from '../../MetricsExcel';
import { Menu } from 'Molecules/Menu';
import { FrequencyEnums } from 'containers/Esrs/pieces';
import { useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { GetDrDatapointsTasksQuery_, TaskStatusEnum_Enum_ } from 'models';

export const TASK_STATUS_TAG = {
  [TaskStatusEnum_Enum_.Completed_]: { variant: 'success', title: 'Completed' },
  [TaskStatusEnum_Enum_.InProgress_]: { variant: 'info', title: 'In progress' },
  [TaskStatusEnum_Enum_.NotStarted_]: { variant: 'undefined', title: 'Not started' },
};

export const MetricTableHeader = ({
  metric,
  setSelectedExcelMetric,
  onImportOpen,
  drRef,
  companyName,
  metricStatus,
  companyAssessmentId,
  tasksLoading,
  companyLevelReportingUnitId,
}: {
  metric: MetricsTableData;
  setSelectedExcelMetric: (value: React.SetStateAction<MetricsTableData | undefined>) => void;
  onImportOpen: () => void;
  drRef: string;
  companyName: string;
  metricStatus: TaskStatusEnum_Enum_ | undefined;
  taskData?: GetDrDatapointsTasksQuery_;
  companyAssessmentId: string;
  tasksLoading: boolean;
  companyLevelReportingUnitId: string;
}) => {
  const toast = useToast();
  const exportMetrics = useExportEsrsMetricTable();
  const { framework } = useParams();

  const isTableMetricYearly = useMemo(
    () => metric?.metric.materialMetrics.every((m) => m?.frequency === FrequencyEnums.yearly),
    [metric]
  );
  const isTableMetricQuarterly = useMemo(
    () => metric?.metric.materialMetrics.every((m) => m?.frequency === FrequencyEnums.quarterly),
    [metric]
  );

  const metricRefNumber = useMemo(() => getMetricRefNumber(metric.metric), [metric]);
  const metricFrameworkRef = metric?.metric?.requirement?.frameworkRef;

  return (
    <HStack justifyContent="space-between" w="100%">
      <LinkAnchor
        hideIcon
        id={metric.metric.reference}
        text={`${metric.metric.title}`}
        reference={
          metricFrameworkRef !== framework
            ? `${metricFrameworkRef} ${metricRefNumber}`
            : metricRefNumber
        }
      />

      <HStack spacing="4px" alignItems="center">
        <Skeleton isLoaded={!tasksLoading}>
          {metricStatus ? (
            <Tag
              verticalAlign="middle"
              variant={TASK_STATUS_TAG[metricStatus].variant}
              title={TASK_STATUS_TAG[metricStatus].title}
              size="xs"
              whiteSpace="nowrap"
              noOfLines={1}
            />
          ) : (
            <></>
          )}
        </Skeleton>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<UploadIcon />}
          onClick={() => {
            onImportOpen();
            setSelectedExcelMetric(metric);
          }}
        >
          Import
        </Button>
        <Menu
          size="sm"
          sections={[
            {
              actions: [
                {
                  id: 'download',
                  title: 'Download Excel file',
                  variant: 'ghost',
                  onClick: () => {
                    toast({
                      text: 'Downloading file...',
                      closable: true,
                      duration: null,
                    });
                    if (metric) {
                      exportMetrics(
                        [metric],
                        drRef ?? '',
                        companyName ?? 'Company',
                        isTableMetricQuarterly,
                        isTableMetricYearly,
                        companyAssessmentId,
                        companyLevelReportingUnitId
                      );
                    }
                  },
                  leftElement: <DownloadIcon />,
                },
              ],
            },
          ]}
        />
      </HStack>
    </HStack>
  );
};
